import axiosInstance from './AxiosInstance';
import { clearCache } from './requestCache';

// A "mock interview" is an event whose category is exactly this — the same
// record the trainer's evaluation screen (/mockinterviews/my-interview-events)
// and the student's list both read. This module is a thin, category-pinned
// view over /events so the Mock Interviews page can do full CRUD without
// having to remember to set (or risk changing) the category on every call.
const API_URL = '/events';
// Turnout/result stats come from the mock-interview module, which owns the
// evaluation records; the CRUD above is on the event record itself.
const STATS_API_URL = '/mockinterviews';
export const MOCK_INTERVIEW_CATEGORY = 'Mock Interview';

const PAGE_CAP = 100;

// The calendar caches its reads for 10s keyed by query params; a mock
// interview is a calendar event, so every write has to drop that cache or the
// calendar serves a pre-write list right after a save.
const invalidateCalendar = () => clearCache('calendar:events:');

export const getMockInterviews = async ({
  limit = 10,
  page = 1,
  search = '',
  searchField = 'name',
  trainerId = null,
  batchId = null,
  status = null,
  // Date range and time-of-day range, both applied server-side.
  dateFrom = null,
  dateTo = null,
  timeFrom = null,
  timeTo = null,
} = {}) => {
  try {
    const params = {
      limit: Math.max(1, Math.min(PAGE_CAP, Number(limit) || 10)),
      page: Math.max(1, Number(page) || 1),
      category: MOCK_INTERVIEW_CATEGORY,
    };

    if (search && String(search).trim() !== '') {
      params.search = String(search).trim();
      params.searchField = searchField || 'name';
    }
    if (trainerId) params.trainerId = trainerId;
    if (batchId) params.batchId = batchId;
    if (status) params.status = status;
    // The events list names its date bounds startDate/endDate.
    if (dateFrom) params.startDate = dateFrom;
    if (dateTo) params.endDate = dateTo;
    if (timeFrom) params.timeFrom = timeFrom;
    if (timeTo) params.timeTo = timeTo;

    const response = await axiosInstance.get(API_URL, { params });
    return {
      data: response.data?.data || [],
      pagination: response.data?.pagination || {},
      message: response.data?.message,
    };
  } catch (error) {
    console.error('Error fetching mock interviews', error);
    throw error;
  }
};

// Turnout and results for a page of mock interviews, keyed by event id.
// Kept as its own call rather than folded into the listing so a slow or
// failing stats query can never take the list down with it.
export const getMockInterviewEventStats = async (eventIds = []) => {
  const ids = (Array.isArray(eventIds) ? eventIds : [eventIds])
    .map((id) => String(id || '').trim())
    .filter(Boolean);
  if (!ids.length) return {};

  try {
    const response = await axiosInstance.get(`${STATS_API_URL}/event-stats`, {
      params: { eventIds: ids.join(',') },
    });
    return response.data?.data || {};
  } catch (error) {
    console.error('Error fetching mock interview event stats', error);
    return {};
  }
};

export const getMockInterviewById = async (id) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching mock interview', error);
    throw error;
  }
};

export const createMockInterview = async (payload) => {
  try {
    const response = await axiosInstance.post(API_URL, {
      ...payload,
      category: MOCK_INTERVIEW_CATEGORY,
    });
    invalidateCalendar();
    return response.data;
  } catch (error) {
    console.error('Error creating mock interview', error);
    throw error;
  }
};

export const updateMockInterview = async (id, payload) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${id}`, {
      ...payload,
      category: MOCK_INTERVIEW_CATEGORY,
    });
    invalidateCalendar();
    return response.data;
  } catch (error) {
    console.error('Error updating mock interview', error);
    throw error;
  }
};

export const deleteMockInterview = async (id) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/${id}`);
    invalidateCalendar();
    return response.data;
  } catch (error) {
    console.error('Error deleting mock interview', error);
    throw error;
  }
};
